import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import CategoryIcon from "@mui/icons-material/Category"
import ListIcon from "@mui/icons-material/List"
import { VisualEditorProvider, useVisualEditor } from "./VisualEditorContext"
import SectionsTab from "./components/SectionsTab"
import StepsTab from "./components/StepsTab"
import { patchProduct, getTypes, getMaterials, getProducts } from "../../../helpers/api_helper"

const VisualEditorContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { productId: urlProductId } = useParams()

  const {
    productId,
    productName,
    steps,
    activeTab,
    isDirty,
    isLoading,
    isSaving,
    setProductData,
    setActiveTab,
    setLoading,
    setSaving,
  } = useVisualEditor()

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [confirmDialog, setConfirmDialog] = useState(false)

  // Initialize data
  useEffect(() => {
    const initData = async () => {
      setLoading(true)

      try {
        // Check if data was passed via location state
        const passedProduct = location.state?.product
        const passedTypes = location.state?.types
        const passedMaterials = location.state?.materialsList

        if (passedProduct && passedProduct.id) {
          // We have product data from MuiProductUpdate, but may need to fetch types/materials
          let typesToUse = passedTypes || []
          let materialsToUse = passedMaterials || []

          // Fetch types and materials if not passed
          if (!passedTypes?.length || !passedMaterials?.length) {
            const [typesRes, materialsRes] = await Promise.all([
              !passedTypes?.length ? getTypes() : Promise.resolve(passedTypes),
              !passedMaterials?.length ? getMaterials() : Promise.resolve(passedMaterials),
            ])
            typesToUse = typesRes || []
            materialsToUse = materialsRes || []
          }

          setProductData({
            productId: passedProduct.id,
            productName: passedProduct.name,
            steps: passedProduct.steps || [],
            types: typesToUse,
            materialsList: materialsToUse,
          })
        } else if (urlProductId && urlProductId !== "undefined") {
          // Fetch all data from API
          const [typesRes, materialsRes, productsRes] = await Promise.all([
            getTypes(),
            getMaterials(),
            getProducts(),
          ])

          const product = productsRes?.find((p) => p.id === parseInt(urlProductId))
          if (product) {
            let parsedSteps = []
            if (product.requirements) {
              try {
                parsedSteps = JSON.parse(product.requirements)
              } catch {
                parsedSteps = []
              }
            }

            setProductData({
              productId: product.id,
              productName: product.name,
              steps: parsedSteps,
              types: typesRes || [],
              materialsList: materialsRes || [],
            })
          } else {
            setSnackbar({
              open: true,
              message: t("Product not found"),
              severity: "error",
            })
          }
        } else {
          setSnackbar({
            open: true,
            message: t("Invalid product ID"),
            severity: "error",
          })
        }
      } catch (err) {
        console.error("Error loading data:", err)
        setSnackbar({
          open: true,
          message: t("Error loading data"),
          severity: "error",
        })
      } finally {
        setLoading(false)
      }
    }

    initData()
  }, [urlProductId, location.state])

  const handleSave = async () => {
    if (!productId) {
      setSnackbar({
        open: true,
        message: t("No product ID found"),
        severity: "error",
      })
      return
    }

    setSaving(true)
    try {
      // Update sequence numbers before saving
      const updatedSteps = steps.map((step, index) => ({
        ...step,
        seq: index + 1,
      }))

      const payload = {
        resource: [
          {
            id: productId,
            requirements: JSON.stringify(updatedSteps),
          },
        ],
      }

      const res = await patchProduct(payload)

      if (res && res.error) {
        setSnackbar({
          open: true,
          message: res.error.message || t("Error saving changes"),
          severity: "error",
        })
      } else {
        setSnackbar({
          open: true,
          message: t("Changes saved successfully"),
          severity: "success",
        })

        // Navigate back with updated steps
        setTimeout(() => {
          navigate("/product-update", {
            state: {
              product: {
                ...location.state?.product,
                id: productId,
                name: productName,
                requirements: JSON.stringify(updatedSteps),
              },
              fromVisualEditor: true,
            },
            replace: true,
          })
        }, 500)
      }
    } catch (err) {
      console.error("Save error:", err)
      setSnackbar({
        open: true,
        message: t("Error saving changes"),
        severity: "error",
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (isDirty) {
      setConfirmDialog(true)
    } else {
      navigate(-1)
    }
  }

  const handleConfirmCancel = () => {
    setConfirmDialog(false)
    navigate(-1)
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Fullscreen Header */}
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          p: 2,
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={handleCancel} sx={{ color: "#64748b" }}>
              <CloseIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1e293b">
                {t("Visual Editor")}
              </Typography>
              <Typography variant="caption" color="#64748b">
                {productName}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                borderColor: "#e2e8f0",
                color: "#64748b",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              sx={{
                bgcolor: "#3b82f6",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#2563eb" },
                "&:disabled": { bgcolor: "#94a3b8" },
              }}
            >
              {isSaving ? t("Saving...") : t("Save Changes")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<CategoryIcon sx={{ fontSize: 20 }} />}
            label={t("Sections")}
            iconPosition="start"
          />
          <Tab
            icon={<ListIcon sx={{ fontSize: 20 }} />}
            label={t("Steps")}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 3 }}>
        {activeTab === 0 && <SectionsTab />}
        {activeTab === 1 && <StepsTab />}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("Unsaved Changes")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("You have unsaved changes. Are you sure you want to leave?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmDialog(false)}
            sx={{ textTransform: "none" }}
          >
            {t("Stay")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            sx={{ textTransform: "none" }}
          >
            {t("Leave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const VisualEditor = () => {
  return (
    <DndProvider backend={HTML5Backend}>
      <VisualEditorProvider>
        <VisualEditorContent />
      </VisualEditorProvider>
    </DndProvider>
  )
}

export default VisualEditor
